/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useCallback, useState} from 'react';
import {useAgentsAPI} from './useAgentsAPI';
import {APIParams, METHODS} from '../../../hooks/abstractions/useTrusstedAgent';
import type {AgentDocument, AgentDocumentRemote} from '../../../types/agent';
import {AGENTS_API_EVENT_NAMES} from '../../../types/agents-api';
import {mapAgentDocumentRemoteToLocal} from '../utils';

export const useGetAgentDocuments = () => {
  const agentsAPI = useAgentsAPI();
  const [documents, setDocuments] = useState<AgentDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(
    async (agentId: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const result: AgentDocumentRemote[] = await agentsAPI({
          eventName: AGENTS_API_EVENT_NAMES.ListDocuments,
          methodName: METHODS.GET,
          params: {},
          queryParams: {agentId},
        } as APIParams);
        const mappedResult = mapAgentDocumentRemoteToLocal(result);
        const sortedResult = mappedResult.sort((a, b) => b.updatedAt - a.updatedAt);
        setDocuments(sortedResult);
        setIsLoading(false);
      } catch (err) {
        setError('Error fetching documents');
        setIsLoading(false);
      }
    },
    [agentsAPI],
  );

  return {
    documents,
    isLoading,
    error,
    fetch,
  };
};
